/**
 * The four launch characters (VISION, design/BRIEF.md "Scope").
 *
 * In SimState a character is stored as its numeric `index` (CharacterIndex). Strings (`CharacterId`) are used on
 * the network and in the UI/asset layer.
 */
import { SpecialId } from './enums';
import { deepFreeze } from './freeze';

export const CHARACTER_IDS = [
  'rookie',
  'bigtex',
  'zorg',
  'grandpa',
  'turbo',
  'dome',
  'rumble',
  'hopper',
  'lodestone',
] as const;
export type CharacterId = (typeof CHARACTER_IDS)[number];
/** Numeric character index stored in SimState (position in CHARACTER_IDS). */
export type CharacterIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

/** 1–5 rating per stat (design sheet "02 Characters"). */
export interface CharacterStats {
  /** Run / dash speed. */
  readonly spd: number;
  /** Jump velocity. */
  readonly jmp: number;
  /** Throw power (pass speed/range). */
  readonly arm: number;
  /** Tackle reach and tackle-break resistance. */
  readonly tkl: number;
  /** Toss accuracy/power (field goals, PAT). */
  readonly toss: number;
}

export interface CharacterDef {
  readonly id: CharacterId;
  readonly index: CharacterIndex;
  readonly name: string;
  /** Short role label for character select. */
  readonly role: string;
  readonly bio: string;
  readonly stats: CharacterStats;
  readonly signature: SpecialId;
  /** Casual-mode head radius multiplier. Ranked always uses 1. */
  readonly headScale: number;
  /** Asset key prefix used by the client asset manifest (e.g. `rookie` → `expr/rookie-determined`). */
  readonly assetKey: string;
  /** Accent color for UI (hex). */
  readonly accent: string;
}

export const CHARACTERS: readonly [
  CharacterDef,
  CharacterDef,
  CharacterDef,
  CharacterDef,
  CharacterDef,
  CharacterDef,
  CharacterDef,
  CharacterDef,
  CharacterDef,
] = deepFreeze([
  {
    id: 'rookie',
    index: 0,
    name: 'THE ROOKIE',
    role: 'All-rounder QB',
    bio: 'Cocky young gunslinger. Spiky hair, eye black, red mouthguard. Throws it deep and chases it himself.',
    stats: { spd: 3, jmp: 3, arm: 4, tkl: 3, toss: 3 },
    signature: SpecialId.HAIL_MARY,
    headScale: 1,
    assetKey: 'rookie',
    accent: '#ffd23f',
  },
  {
    id: 'bigtex',
    index: 1,
    name: 'BIG TOM',
    role: 'Power runner',
    bio: 'Old-school lineman in a leather helmet with a handlebar mustache. Runs through people, not around them.',
    stats: { spd: 1, jmp: 2, arm: 3, tkl: 5, toss: 4 },
    signature: SpecialId.BULLDOZER,
    headScale: 1.1,
    assetKey: 'bigtex',
    accent: '#ff8a1f',
  },
  {
    id: 'zorg',
    index: 2,
    name: 'ZORG',
    role: 'Trickster',
    bio: 'Green alien with a tiny silver helmet between his antennae. Floaty jumps and mind games.',
    stats: { spd: 4, jmp: 5, arm: 3, tkl: 2, toss: 2 },
    signature: SpecialId.MIRAGE,
    headScale: 0.95,
    assetKey: 'zorg',
    accent: '#b6ff3b',
  },
  {
    id: 'grandpa',
    index: 3,
    name: 'GRANDPA GRIT',
    role: 'Lockdown defender',
    bio: 'Grumpy coach-turned-defender. Bald, red headset, whistle. Has seen every trick since 1958.',
    stats: { spd: 2, jmp: 2, arm: 3, tkl: 5, toss: 3 },
    signature: SpecialId.BLITZ,
    headScale: 1,
    assetKey: 'grandpa',
    accent: '#4ee6ff',
  },
  {
    id: 'turbo',
    index: 4,
    name: 'TURBO TUCK',
    role: 'Speed back',
    bio: 'Strapped go-kart rockets to his cleats against every safety recommendation. Three steps, then gone.',
    stats: { spd: 5, jmp: 3, arm: 2, tkl: 1, toss: 2 },
    signature: SpecialId.ROCKET_BOOTS,
    headScale: 1,
    assetKey: 'turbo',
    accent: '#ff4fd8',
  },
  {
    id: 'dome',
    index: 5,
    name: 'DOME DUGAN',
    role: 'Mascot linebacker',
    bio: 'The costume ripped; turns out his head was always this size. Nothing gets past it.',
    stats: { spd: 2, jmp: 3, arm: 2, tkl: 4, toss: 3 },
    signature: SpecialId.BIG_HEAD,
    headScale: 1.1,
    assetKey: 'dome',
    accent: '#b36bff',
  },
  {
    id: 'rumble',
    index: 6,
    name: 'RUMBLE ROSS',
    role: 'Enforcer safety',
    bio: 'Stomps the turf like a war drum. Ball carriers on the ground suddenly remember they have a curfew.',
    stats: { spd: 2, jmp: 2, arm: 2, tkl: 5, toss: 3 },
    signature: SpecialId.GROUND_POUND,
    headScale: 1.05,
    assetKey: 'rumble',
    accent: '#ff4b3e',
  },
  {
    id: 'hopper',
    index: 7,
    name: 'HOPPER LEE',
    role: 'Acrobat returner',
    bio: 'Grew up on a trampoline and never really left. One jump never seemed like enough.',
    stats: { spd: 3, jmp: 5, arm: 2, tkl: 2, toss: 2 },
    signature: SpecialId.DOUBLE_JUMP,
    headScale: 0.95,
    assetKey: 'hopper',
    accent: '#7ef9a0',
  },
  {
    id: 'lodestone',
    index: 8,
    name: 'LODESTONE LOU',
    role: 'Ball-hawk tinkerer',
    bio: 'Rewired a glove with scrapyard magnets in the garage. Nobody believes him until the ball just arrives.',
    stats: { spd: 3, jmp: 3, arm: 3, tkl: 3, toss: 3 },
    signature: SpecialId.MAGNET_ONSIDE,
    headScale: 1,
    assetKey: 'lodestone',
    accent: '#5c6bff',
  },
]);

export const characterIndexOf = (id: CharacterId): CharacterIndex =>
  CHARACTER_IDS.indexOf(id) as CharacterIndex;
export const characterById = (id: CharacterId): CharacterDef => CHARACTERS[characterIndexOf(id)];
export const characterByIndex = (index: number): CharacterDef =>
  CHARACTERS[index as CharacterIndex] ?? CHARACTERS[0];
export const isCharacterId = (v: unknown): v is CharacterId =>
  typeof v === 'string' && (CHARACTER_IDS as readonly string[]).includes(v);
